import { db, collection, getDocs, query, where } from './FirebaseService';

const specialtyService = {
  // Mapeo de nombres de Firebase a IDs de Odoo
  nameToOdooMapping: {
    // Especialidades principales
    'ginecología': 15,
    'ginecologia': 15,
    'psicología': 16,
    'psicologia': 16,
    'nutrición': 17,
    'nutricion': 17,
    'salud general': 18,
    'skincare': 18,
    'entrenamiento': 28,
    'dermatología': 19,
    'dermatologia': 19,
    'hematología': 20,
    'hematologia': 20,
    'oncología': 21,
    'oncologia': 21,
    'reumatología': 22,
    'reumatologia': 22,
    'endocrinología': 23,
    'endocrinologia': 23,
    'psiquiatría': 24,
    'psiquiatria': 24,
    'angiología': 25,
    'angiologia': 25,
    'tanatología': 26,
    'tanatologia': 26,
    'terapia en pareja': 27,
    'cardiología': 29,
    'cardiologia': 29,
    'fisioterapia': 30,
    'oftalmología': 31,
    'oftalmologia': 31,
    'ortopedia': 32,
    'traumatología': 32,
    'traumatologia': 32,
    'otorrinolaringología': 33,
    'otorrinolaringologia': 33
    // Nota: especialidades nuevas (nefrología, urología, etc.) se resuelven automáticamente
    // por su firebaseId numérico — no es necesario agregarlas aquí mientras el doc.id de
    // Firestore coincida con el ID de Odoo (convención existente en la base).
  },


  async getOnDemandSpecialties() {
    try {
      const specialtiesQuery = query(
        collection(db, 'dais_specialty'),
        where('active', '==', true)
      );

      const querySnapshot = await getDocs(specialtiesQuery);
      
      const specialties = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        specialties.push({
          firebaseId: doc.id,
          ...data
        });
      });
      // Log de todas las especialidades
      specialties.forEach((spec, index) => {
      });

      return specialties;
    } catch (error) {
      throw new Error('No se pudieron cargar las especialidades');
    }
  },

  // Resuelve el ID de Odoo de una especialidad de Firebase.
  // Prioridad: 1) firebaseId numérico (es la verdad — el doc.id en Firestore = Odoo ID)
  //            2) mapping por nombre (compatibilidad con docs viejos)
  //            3) null — el caller debe filtrar la especialidad en lugar de caer a un default
  //               (el fallback a 18 causaba que nefrología/dermatología/otras especialidades sin
  //               mapping mostraran doctoras de Salud General).
  getOdooId(firebaseId, specialtyName) {
    const parsedId = parseInt(firebaseId, 10);
    if (!Number.isNaN(parsedId) && parsedId > 0) {
      return parsedId;
    }

    if (specialtyName) {
      const nameLower = specialtyName.toLowerCase();
      if (this.nameToOdooMapping[nameLower]) {
        return this.nameToOdooMapping[nameLower];
      }
      for (const [key, value] of Object.entries(this.nameToOdooMapping)) {
        if (nameLower.includes(key)) {
          return value;
        }
      }
    }

    return null;
  },

  // Design system: sin emojis. Los iconos de especialidad ahora son lucide-react
  // en los componentes (mapeados por nombre). Devolvemos cadena vacía para que
  // ningún emoji se filtre por el campo `icon` de los objetos de especialidad.
  getSpecialtyEmoji(specialtyName) {
    return '';
  },

  getIncidence(specialtyName) {
    const highIncidence = ['ginecologia', 'psicologia', 'psicología', 'ginecología'];
    const mediumIncidence = ['nutricion', 'nutrición', 'salud general', 'skincare', 'dermatologia', 'dermatología'];
    
    const nameLower = specialtyName.toLowerCase();
    
    if (highIncidence.some(term => nameLower.includes(term))) return 'high';
    if (mediumIncidence.some(term => nameLower.includes(term))) return 'medium';
    return 'low';
  },

  // FUNCIÓN CLAVE: Formatear especialidades usando odooId como ID principal
  formatSpecialtiesForUI(firebaseSpecialties) {
    return firebaseSpecialties.map(firebaseSpec => {
      // 1. Obtener ID de Odoo (puede ser null si no se puede resolver)
      const odooId = this.getOdooId(firebaseSpec.firebaseId, firebaseSpec.name);

      // Si no hay un ID Odoo confiable, descartamos la especialidad: mostrarla causaría
      // consultas a IDs incorrectos (era el bug de "nefrología muestra Salud General").
      if (!odooId) {
        console.warn('[specialtyService] Especialidad sin Odoo ID válido — descartada:', firebaseSpec.name, firebaseSpec.firebaseId);
        return null;
      }

      // 2. Obtener duración desde Firebase (durations = incluidas, extra_durations = sin membresía)
      const includedDurations = firebaseSpec.durations || [];
      const extraDurations = firebaseSpec.extra_durations || [];
      const durationId = includedDurations[0]?.id || extraDurations[0]?.id || 28;
      const extraDurationId = extraDurations[0]?.id || null;

      // 3. IMPORTANTE: Usar odooId como ID principal
      const specialtyId = odooId;
      return {
        // ID PRINCIPAL: odooId (ej: 16)
        id: specialtyId,
        
        // Datos de referencia
        firebaseId: firebaseSpec.firebaseId,
        odooId: odooId,
        durationId: durationId,
        extraDurationId: extraDurationId,
        
        // Datos básicos
        name: firebaseSpec.name || 'Sin nombre',
        name_EN: firebaseSpec.name_EN || '',
        icon: this.getSpecialtyEmoji(firebaseSpec.name),
        incidence: this.getIncidence(firebaseSpec.name),
        
        // Configuración
        active: firebaseSpec.active || false,
        include_in_membership: firebaseSpec.include_in_membership || false,
        
        // Datos originales
        firebaseData: firebaseSpec,
        
        // Metadata
        icon_url: firebaseSpec.icon || '',
        photo_url: firebaseSpec.photo_url || '',
        price_extra_consultations: firebaseSpec.price_extra_consultations || [],
        durations: includedDurations,
        extra_durations: extraDurations,
        can_prescribe: firebaseSpec.can_prescribe || false,
        gender_ids: firebaseSpec.gender_ids || [],
        tag_ids: firebaseSpec.tag_ids || [],
        terms_and_conditions: firebaseSpec.terms_and_conditions || '',
        url_record_icon: firebaseSpec.url_record_icon || ''
      };
    }).filter(Boolean);
  },

  async getFormattedOnDemandSpecialties() {
    try {
      const firebaseSpecialties = await this.getOnDemandSpecialties();
      const formatted = this.formatSpecialtiesForUI(firebaseSpecialties);
      return formatted;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Obtener configuración de especialidades para App.js
   * Retorna: { 16: {id: 16, duration: 29, name: "Psicología", ...}, ... }
   */
  async getSpecialtyConfig() {
    try {
      const specialties = await this.getFormattedOnDemandSpecialties();
      
      const config = {};
      specialties.forEach(specialty => {
        // Usar odooId como key (ej: 16)
        config[specialty.odooId] = {
          id: specialty.odooId,              // 16
          duration: specialty.durationId,     // included duration (from Firebase durations[])
          extraDuration: specialty.extraDurationId, // extra duration (from Firebase extra_durations[])
          name: specialty.name,              // "Psicología"
          icon: specialty.icon,              // "💭"
          firebaseId: specialty.firebaseId,  // "16"
          firebaseData: specialty.firebaseData,
          durations: specialty.durations,             // full durations array
          extra_durations: specialty.extra_durations,  // full extra_durations array
          price_extra_consultations: specialty.price_extra_consultations // precios extra (keyados por minutos)
        };
      });
      return config;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Obtener lista de especialidades para UI
   * Retorna array con IDs numéricos
   */
  async getSpecialtiesForUI() {
    try {
      const specialties = await this.getFormattedOnDemandSpecialties();
      
      return specialties.map(specialty => ({
        id: specialty.id,         // 16 (odooId)
        name: specialty.name,     // "Psicología"
        icon: specialty.icon,     // "💭"
        incidence: specialty.incidence, // "high"
        include_in_membership: specialty.include_in_membership,
        odooId: specialty.odooId, // 16 (igual que id)
        firebaseId: specialty.firebaseId, // "16"
        durationId: specialty.durationId,        // included duration
        extraDurationId: specialty.extraDurationId // extra duration (sin membresía)
      }));
    } catch (error) {
      throw error;
    }
  },

  /**
   * Obtener especialidad por ID (ID numérico)
   */
  async getSpecialtyById(specialtyId) {
    try {
      const specialties = await this.getFormattedOnDemandSpecialties();
      return specialties.find(specialty => specialty.id === specialtyId) || null;
    } catch (error) {
      throw error;
    }
  }
};

export default specialtyService;